"use client";

import React from "react";
import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
  DOMNode,
  Text as DomHandlerText,
} from "html-react-parser";
import DOMPurify from "dompurify";

type TagFunctionReturn =
  | React.ReactElement
  | string
  | boolean
  | object
  | void
  | null;
type TagFunction = (
  children: React.ReactNode,
  attribs: Record<string, string>,
) => TagFunctionReturn;
type TagMap = Record<string, TagFunction>;

type RichTextProps = {
  children: string | React.ReactNode;
  components?: TagMap;
};

export default function RichText({ children, components = {} }: RichTextProps) {
  if (typeof children !== "string") return <>{children}</>;

  const sanitized = DOMPurify.sanitize(children, { ADD_TAGS: ["x-link"] });

  const options: HTMLReactParserOptions = {
    replace(domNode: DOMNode) {
      if (domNode.type === "tag" && domNode instanceof Element) {
        const tag = domNode.name;
        const attribs = domNode.attribs || {};
        const tagFn = components[tag];
        if (tagFn) {
          return tagFn(
            domToReact(domNode.children as unknown as DOMNode[], options),
            attribs,
          );
        }
      }
      if (domNode.type === "text") {
        const text = (domNode as DomHandlerText).data;
        if (text) {
          return <span>{text}</span>;
        }
        return null;
      }
      return undefined;
    },
  };

  return <>{parse(sanitized, options)}</>;
}
